import { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react'; // Import useCallback
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/api';

// Helper function to decode JWT payload
const decodeJwtPayload = (token) => {
    //console.log('AuthContext: Attempting to decode token payload.');
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const payload = JSON.parse(jsonPayload);
        //console.log('Decoded JWT payload:', payload); // Add log for decoded payload
        return payload;
    } catch (e) {
        console.error('AuthContext: Failed to decode JWT payload:', e); // Modified log
        return null;
    }
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false); // New state for session expiration
    const navigate = useNavigate();

    const login = useCallback((data) => {
        return new Promise((resolve, reject) => {
            let token, payload;

            if (typeof data === 'string') {
                // Pi authentication: data is a JWT token string
                token = data;
                payload = decodeJwtPayload(token);
            } else if (typeof data === 'object' && data !== null) {
                // Google authentication: data is a user object from the cookie
                payload = data;
                token = null; // No token in session storage for Google Auth
            } else {
                console.error('AuthContext: Invalid login data:', data);
                reject(new Error('Invalid login data'));
                return;
            }

            if (!payload || (payload.exp && payload.exp * 1000 <= Date.now())) {
                console.error('AuthContext: Invalid or expired token payload:', payload);
                sessionStorage.removeItem('jwt_token');
                setAuthState(null);
                setSessionExpired(true);
                sessionStorage.setItem('logout_message', 'Your session has expired. Please log in again.');
                navigate('/login');
                reject(new Error('Invalid or expired token'));
                return;
            }

            // Only set sessionStorage for Pi auth
            if (typeof data === 'string') {
                sessionStorage.setItem('jwt_token', token);
            }

            const authDetails = {
                token, // This will be null for Google auth, which is fine
                user: {
                    id: payload.id,
                    provider: payload.provider,
                    providerId: payload.providerId,
                    roles: payload.roles, // Use the new 'roles' array
                    agentId: payload.agentId,
                    sellerId: payload.sellerId,
                    username: payload.username,
                    email: payload.email,
                    profile_picture_url: payload.profile_picture_url, // Add profile image
                    bio: payload.bio,
                    profession: payload.profession,
                    is_legacy_creator: payload.is_legacy_creator,
                    has_pulse_hub_access: payload.has_pulse_hub_access
                },
                expiresAt: payload.exp ? payload.exp * 1000 : Date.now() + 8 * 60 * 60 * 1000 // 8 hours
            };

            setAuthState(authDetails);
            setSessionExpired(false);
            sessionStorage.removeItem('logout_message');


            resolve(authDetails);
        });
    }, [navigate]);

    // Effect to initialize authState from cookie or sessionStorage on mount
    useEffect(() => {
        const initializeAuth = async () => {
            setLoading(true);
            //console.log('[AUTH-CONTEXT] 🔄 STARTING auth initialization...');

            // PRIORITY 1: Check for JWT token in sessionStorage (Pi auth - has complete roles data)
            const token = sessionStorage.getItem('jwt_token');
            if (token) {
                //console.log('[AUTH-CONTEXT] 🔑 Found JWT token in sessionStorage');
                const payload = decodeJwtPayload(token);

                if (payload && payload.exp * 1000 > Date.now()) {
                    //console.log('[AUTH-CONTEXT] ✅ JWT valid, using token with roles:', payload.roles);
                    await login(token);

                    // Check for redirect destination
                    const redirectDestination = sessionStorage.getItem('auth_redirect_destination');
                    if (redirectDestination) {
                        sessionStorage.removeItem('auth_redirect_destination');
                        navigate(redirectDestination, { replace: true });
                    }
                    setLoading(false);
                    return; // Exit early - we have complete auth data from JWT
                } else {
                    //console.log('[AUTH-CONTEXT] ⏰ JWT expired, removing and falling back to cookie auth');
                    sessionStorage.removeItem('jwt_token');
                }
            }

            // PRIORITY 2: Try cookie-based auth via /api/auth/me (Google auth)
            try {
                //console.log('[AUTH-CONTEXT] 📡 No valid JWT, trying /api/auth/me for cookie auth...');
                const { data } = await axiosInstance.get('/api/auth/me');
                //console.log('[AUTH-CONTEXT] ✅ Cookie auth successful, user:', data.user?.username);

                await login(data.user);

                // Check for redirect destination
                const redirectDestination = sessionStorage.getItem('auth_redirect_destination');
                if (redirectDestination) {
                    sessionStorage.removeItem('auth_redirect_destination');
                    navigate(redirectDestination, { replace: true });
                }
            } catch (error) {
                // console.log('[AUTH-CONTEXT] ❌ No valid authentication found:', error.message);
                setAuthState(null);
                // Do not reset userMode here to preserve preference across refreshes/expirations
            } finally {
                setLoading(false);
                //console.log('[AUTH-CONTEXT] ✅ Auth initialization complete at:', new Date().toISOString());
            }
        };

        initializeAuth();
    }, [login, navigate]);

    // Add isAuthenticated function
    const isAuthenticated = useCallback(() => {
        return !!authState;
    }, [authState]);

    const [userMode, setUserMode] = useState(() => {
        return sessionStorage.getItem('userMode') || 'buyer';
    });

    // Effect to handle Google OAuth redirect from URL query parameter
    useEffect(() => {
        const handleGoogleOAuthRedirect = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const redirectPath = urlParams.get('redirect');

            if (redirectPath && isAuthenticated()) {
                // console.log('[AUTH-CONTEXT] Navigating to:', redirectPath);
                // Clean up the URL by removing the redirect parameter
                window.history.replaceState({}, document.title, window.location.pathname);
                // Navigate to the intended destination
                navigate(redirectPath, { replace: true });
            } else if (redirectPath && !isAuthenticated()) {
                //console.log('[AUTH-CONTEXT] Not authenticated yet, waiting for auth...');
            }
        };

        // Only run this effect when the component mounts and we're on a page with redirect query
        if (window.location.search.includes('redirect=')) {
            handleGoogleOAuthRedirect();
        }
    }, [isAuthenticated, navigate]);

    // Modified logout function to accept message and redirectPath
    const logout = useCallback(async (message = null, redirectPath = '/', navState = null) => {
        try {
            await axiosInstance.post('/api/auth/logout');
        } catch (error) {
            console.error('Logout failed', error);
        }
        setAuthState(null);
        sessionStorage.removeItem('jwt_token');
        // Do not remove userMode from sessionStorage to preserve preference

        if (message) {
            sessionStorage.setItem('logout_message', message);
        } else {
            sessionStorage.removeItem('logout_message');
        }
        setSessionExpired(false);

        // Pass navState to navigate if provided (e.g., to preserve 'from' location)
        navigate(redirectPath, { replace: true, state: navState });
    }, [navigate]);

    // Add manual token refresh functionality
    const refreshAccessToken = useCallback(async () => {
        try {
            const response = await axiosInstance.post('/api/auth/refresh');
            const { success } = response.data;

            if (success) {
                // Token automatically updated via cookie
                // Re-fetch user data to get updated token expiry
                const { data } = await axiosInstance.get('/api/auth/me');
                login(data.user);
                return true;
            }
        } catch (error) {
            console.error('Token refresh failed:', error);
            return false;
        }
    }, [login]);

    const switchMode = useCallback((newMode) => {
        setUserMode(newMode);
        sessionStorage.setItem('userMode', newMode);
    }, []);

    useEffect(() => {
        sessionStorage.setItem('userMode', userMode);
    }, [userMode]);


    const value = useMemo(() => ({
        authState,
        user: authState?.user, // Add user object to context value
        userMode,
        switchMode,
        username: authState?.user?.username, // Derive username directly from authState
        login,
        logout,
        refreshAccessToken,
        isAuthenticated,
        loading,
        sessionExpired, // Expose sessionExpired state
    }), [authState, userMode, switchMode, authState?.user?.username, login, logout, refreshAccessToken, isAuthenticated, loading, sessionExpired]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;